import type { MeetingPage, MeetingSummary } from '@fruuit/shared'
import {
  Button,
  Callout,
  cn,
  EmptyState,
  IconButton,
  LoadingRegion,
  PageHeader,
  Select,
  StatusBadge,
  useToast,
} from '@fruuit/ui'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, ChevronLeft, ChevronRight, LayoutGrid, List } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { type ApiError, errorMessage } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { useSession } from '../../auth/session-context'
import { useTitle } from '../../lib/useTitle'
import { type CalendarArea, fetchMeetingRange, meetingCommands, meetingKeys } from '../meetings/api'
import { MeetingFormDialog } from '../meetings/MeetingFormDialog'
import {
  addMonths,
  dateTimeIn,
  dayNumber,
  monthCells,
  monthLabel,
  monthOf,
  monthRange,
  rangeIn,
  rangeUtc,
  shortMonth,
  timeIn,
  timeUtc,
  WEEKDAYS,
} from './time'

type Mode = 'month' | 'agenda'

function prefersAgenda(): boolean {
  try {
    return window.matchMedia?.('(max-width: 640px)').matches ?? false
  } catch {
    return false
  }
}

export function meetingStatusLabel(m: MeetingSummary): string {
  if (m.state === 'draft') return 'Team draft'
  if (m.state === 'cancelled') return 'Cancelled'
  if (m.state === 'superseded') return 'Rescheduled'
  if (m.minutes_status === 'published') return 'Reviewed minutes available'
  return 'Agenda available'
}

/**
 * S6 Calendar (CAL-036, P-CAL-003): a Monday-first month grid in UTC and an
 * agenda list, filtered by engagement and company. Investors see published
 * meetings of their engagements; the case team also sees drafts, cancellations
 * and rescheduled meetings. Defaults to the server's current month (CAL-072).
 */
export function CalendarView({ area }: { area: CalendarArea }) {
  useTitle('Calendar')
  const staff = area === 'workspace'
  const me = useSession()
  const navigate = useNavigate()
  const { toast } = useToast()
  const isManager = me.memberships.some((m) => m.role === 'manager')
  const [month, setMonth] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>(() => (prefersAgenda() ? 'agenda' : 'month'))
  const [engagement, setEngagement] = useState('')
  const [company, setCompany] = useState('')
  const [scheduling, setScheduling] = useState(false)

  const shown = month ?? monthOf(new Date().toISOString())
  const range = monthRange(shown)
  const query = useQuery<MeetingPage, ApiError>({
    queryKey: meetingKeys.list(area, range.from, range.to),
    queryFn: ({ signal }) => fetchMeetingRange(area, range.from, range.to, signal),
  })
  // Follow the server clock for "this month" until the user navigates (CAL-072).
  const serverMonth = query.data ? monthOf(query.data.server_time) : null
  useEffect(() => {
    if (month === null && serverMonth && serverMonth !== shown) setMonth(serverMonth)
  }, [month, serverMonth, shown])
  const today = query.data?.server_time.slice(0, 10) ?? null

  const all = query.data?.items ?? []
  const engagementOptions = useMemo(
    () =>
      [...new Map(all.map((m) => [m.engagement.id, m.engagement.title])).entries()].map(
        ([value, label]) => ({ value, label }),
      ),
    [all],
  )
  const companyOptions = useMemo(
    () =>
      [...new Map(all.map((m) => [m.company.id, m.company.name])).entries()].map(
        ([value, label]) => ({ value, label }),
      ),
    [all],
  )
  const meetings = all.filter(
    (m) =>
      (!engagement || m.engagement.id === engagement) && (!company || m.company.id === company),
  )

  const create = useApiMutation(
    (body: Record<string, unknown>, ctx) => meetingCommands.create(body, ctx),
    {
      invalidate: [meetingKeys.all],
    },
  )

  const base = `/${area}`
  return (
    <>
      <PageHeader
        eyebrow={staff ? 'Meetings & minutes' : 'Meetings & milestones'}
        title={
          staff ? 'Plan, publish and record company meetings.' : 'Make the next conversation count.'
        }
        description={
          staff
            ? 'Draft agendas, publish meetings and take minutes through independent review.'
            : 'Prepare for company dialogue and return to the reviewed record afterwards.'
        }
        actions={
          staff && isManager ? (
            <Button onClick={() => setScheduling(true)}>Schedule meeting</Button>
          ) : null
        }
      />

      <div className="mb-5 flex flex-col gap-4 rounded-lg border border-line bg-surface p-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="flex shrink-0 items-center gap-2">
          <IconButton
            label="Previous month"
            icon={<ChevronLeft />}
            variant="secondary"
            onClick={() => setMonth(addMonths(shown, -1))}
          />
          <h2
            className="min-w-40 text-center font-display text-lg font-semibold"
            aria-live="polite"
          >
            {monthLabel(shown)}
          </h2>
          <IconButton
            label="Next month"
            icon={<ChevronRight />}
            variant="secondary"
            onClick={() => setMonth(addMonths(shown, 1))}
          />
          <span className="ml-2 hidden text-sm whitespace-nowrap text-sage sm:inline">
            Local meeting times · {meetings.length} {meetings.length === 1 ? 'meeting' : 'meetings'}
          </span>
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-3 sm:flex sm:items-end">
          <Select
            className="col-span-2 sm:col-span-1 sm:min-w-0 sm:flex-1"
            label="Engagement"
            placeholder="All engagements"
            options={engagementOptions}
            value={engagement}
            onChange={(e) => setEngagement(e.target.value)}
          />
          <Select
            className="col-span-2 sm:col-span-1 sm:min-w-0 sm:flex-1"
            label="Company"
            placeholder="All companies"
            options={companyOptions}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
          <fieldset
            aria-label="View"
            className="col-span-2 m-0 flex min-w-0 shrink-0 rounded-md border border-line-strong p-0.5 sm:col-span-1"
          >
            {(
              [
                ['month', 'Month', <LayoutGrid key="g" />],
                ['agenda', 'Agenda', <List key="l" />],
              ] as const
            ).map(([value, label, icon]) => (
              <button
                key={value}
                type="button"
                aria-pressed={mode === value}
                onClick={() => setMode(value)}
                className={cn(
                  'inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded px-3 text-sm font-semibold whitespace-nowrap [&>svg]:size-4',
                  mode === value ? 'bg-teal text-white' : 'text-teal hover:bg-wash',
                )}
              >
                {icon}
                {label}
              </button>
            ))}
          </fieldset>
        </div>
      </div>

      {query.isLoading ? (
        <LoadingRegion label="Loading meetings…" />
      ) : query.isError ? (
        <Callout tone="danger" role="alert">
          {errorMessage(query.error)}
        </Callout>
      ) : mode === 'month' ? (
        <MonthGrid month={shown} meetings={meetings} today={today} base={base} />
      ) : (
        <Agenda meetings={meetings} base={base} />
      )}
      {mode === 'month' && !query.isLoading ? (
        <section className="mt-6" aria-labelledby="month-agenda">
          <h2 id="month-agenda" className="mb-3 font-display text-lg font-semibold">
            This month’s agenda
          </h2>
          <Agenda meetings={meetings} base={base} />
        </section>
      ) : null}

      <p className="mt-6 text-xs text-sage">
        Meetings are internal planning records. Fruuit Engage does not send calendar invitations or
        contact a company. Fictional demo data.
      </p>

      {staff ? (
        <MeetingFormDialog
          open={scheduling}
          onClose={() => setScheduling(false)}
          onSubmit={async (body) => {
            const created = await create.mutateAsync(body)
            toast({
              tone: 'success',
              message: 'Draft meeting created. Publish it from the meeting page.',
            })
            navigate(`${base}/meetings/${created.id}`)
          }}
        />
      ) : null}
    </>
  )
}

function MonthGrid({
  month,
  meetings,
  today,
  base,
}: {
  month: string
  meetings: MeetingSummary[]
  today: string | null
  base: string
}) {
  const cells = monthCells(month)
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface">
      <div className="min-w-[680px]">
        <div className="grid grid-cols-7 border-b border-line bg-cream text-xs font-semibold uppercase tracking-[0.08em] text-sage">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-2 py-2">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((date, i) => {
            const items = date ? meetings.filter((m) => m.starts_at.startsWith(date)) : []
            return (
              <div
                key={date ?? `blank-${i}`}
                className={cn(
                  'min-h-24 border-r border-b border-line-soft p-1.5 [&:nth-child(7n)]:border-r-0',
                  !date && 'bg-cream/50',
                  date === today && 'bg-teal-50',
                )}
              >
                {date ? (
                  <span
                    className={cn(
                      'inline-flex size-6 items-center justify-center rounded-full text-xs font-semibold text-ink-soft',
                      date === today && 'bg-teal text-white',
                    )}
                  >
                    {Number(date.slice(8))}
                  </span>
                ) : null}
                <div className="mt-1 flex flex-col gap-1">
                  {items.map((m) => (
                    <Link
                      key={m.id}
                      to={`${base}/meetings/${m.id}`}
                      className={cn(
                        'block truncate rounded px-1.5 py-1 text-xs leading-tight',
                        m.state === 'published' && 'bg-teal text-white hover:bg-teal-800',
                        m.state === 'draft' &&
                          'border border-dashed border-watch bg-watch-soft text-watch',
                        (m.state === 'cancelled' || m.state === 'superseded') &&
                          'bg-quiet-soft text-quiet line-through',
                      )}
                      title={`${dateTimeIn(m.starts_at, m.timezone)} (${timeUtc(m.starts_at)} UTC) ${m.title}`}
                    >
                      <b>{timeIn(m.starts_at, m.timezone)}</b> {m.title}
                    </Link>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function Agenda({ meetings, base }: { meetings: MeetingSummary[]; base: string }) {
  if (meetings.length === 0) {
    return (
      <EmptyState
        icon={<CalendarDays />}
        title="No meetings are recorded for this month."
        description="Use the arrows to view another month."
      />
    )
  }
  return (
    <ul className="flex flex-col divide-y divide-line-soft rounded-lg border border-line bg-surface">
      {meetings.map((m) => (
        <li key={m.id}>
          <Link
            to={`${base}/meetings/${m.id}`}
            className="grid grid-cols-[3.25rem_minmax(0,1fr)] items-center gap-3 p-3 hover:bg-wash sm:grid-cols-[3.25rem_minmax(0,1fr)_auto] sm:gap-4 sm:p-4"
          >
            <span className="flex flex-col items-center rounded-md bg-wash py-1.5 text-teal">
              <b className="font-display text-lg leading-none">{dayNumber(m.starts_at)}</b>
              <small className="text-[11px] uppercase">{shortMonth(m.starts_at)}</small>
            </span>
            <span className="min-w-0">
              <strong
                className={cn(
                  'block truncate',
                  m.state !== 'published' && m.state !== 'draft' && 'line-through',
                )}
              >
                {m.title}
              </strong>
              <small className="block truncate text-sm text-sage">
                {m.company.name} · {m.meeting_type}
              </small>
              <small
                className="block text-sm text-ink-soft sm:hidden"
                title={rangeUtc(m.starts_at, m.ends_at)}
              >
                {rangeIn(m.starts_at, m.ends_at, m.timezone)}
              </small>
            </span>
            <span className="col-span-2 flex flex-wrap items-center gap-2 sm:col-span-1 sm:flex-col sm:items-end">
              <span
                className="hidden text-sm text-ink-soft sm:block"
                title={rangeUtc(m.starts_at, m.ends_at)}
              >
                {rangeIn(m.starts_at, m.ends_at, m.timezone)}
              </span>
              <StatusBadge status={meetingStatusLabel(m)} />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  )
}
